import {
  apiV2DataPropertyListPost,
  apiV2DataMonitorAlarmRuleAddPost,
  apiV2DataMonitorAlarmRuleInfoPost,
  apiV2DataMonitorAlarmRuleUpdatePost,
} from '@maxtropy/device-customer-apis-v2';
import {
  Button,
  defaultErrorHandling,
  Form,
  FormTitle,
  Input,
  InputNumber,
  Modal,
  Select,
  SubContent,
  Tooltip,
  useBreadcrumbRoutes,
  Wrapper,
} from '@maxtropy/components';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Cascader, Col, Row, Space } from 'antd';
import { useRequest } from 'ahooks';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { getDeviceTypeTreeWithoutScene } from '@/api/deviceType';
import { getPhysicalModelList } from '@/shared/api/options';
import { formulaValidator, formatOnSave, parseOnEcho } from '@/shared/components/FormulaInput';
import FormulaInput from '@/shared/components/FormulaInput/FormulaInputOfAlarm';
import { AlarmLevel, AlarmLevelDisplay, DeviceType } from '@/shared/types';
import { formatDeviceTypeToDataNode } from '@/shared/utils/utils';

import { DataMonitoringRuleType, dataMonitoringRuleTypeOptions } from './constants';
import SelectDeviceModal from './SelectDeviceModal';

interface CreateOrEditProps {
  isEdit?: boolean;
}

interface FormValues {
  ruleName: string;
  alarmLevel: AlarmLevel;
  ruleType: DataMonitoringRuleType;
  deviceTypePath: number[];
  deviceIds?: number[];
  physicalModelId?: number;
  monitorDataPropertyIds?: number[];
  monitorTimeRange?: number;
  uploadDataCount?: number;
  alarmFormula?: string;
  alarmDuration?: number;
}

interface DataPropertyOption {
  id: number;
  name: string;
}

const SELECT_ALL_PROPERTIES = -1;

const alarmLevelOptions = Object.values(AlarmLevel)
  .filter(value => typeof value === 'number')
  .map(value => ({
    label: AlarmLevelDisplay[value as AlarmLevel],
    value: value as AlarmLevel,
  }));

const findDeviceTypePath = (tree: DeviceType[], targetId?: number, parentPath: number[] = []): number[] | undefined => {
  if (targetId === undefined) return undefined;

  for (const item of tree) {
    const path = [...parentPath, item.id];
    if (item.id === targetId) return path;

    const children = item.deviceTypes ?? item.children ?? [];
    const childPath = findDeviceTypePath(children, targetId, path);
    if (childPath) return childPath;
  }

  return undefined;
};

const getFormulaPropertyIds = (formula: string) => [
  ...new Set(Array.from(formula.matchAll(/x_(\d+)/g), match => Number(match[1]))),
];

const DataMonitoringAlarmRuleCreateOrEdit: React.FC<CreateOrEditProps> = ({ isEdit = false }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const [form] = Form.useForm<FormValues>();
  const [deviceModalOpen, setDeviceModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const ruleType = Form.useWatch('ruleType', form);
  const deviceTypePath = Form.useWatch('deviceTypePath', form);
  const physicalModelId = Form.useWatch('physicalModelId', form);
  const selectedDeviceIds = Form.useWatch('deviceIds', form) ?? [];
  const monitorDataPropertyIds = Form.useWatch('monitorDataPropertyIds', form) ?? [];
  const alarmFormula = Form.useWatch('alarmFormula', form);
  const selectedDeviceTypeId = deviceTypePath?.[deviceTypePath.length - 1];

  const { data: deviceTypeTreeData } = useRequest(() => getDeviceTypeTreeWithoutScene());
  const deviceTypeTree = useMemo(() => deviceTypeTreeData?.deviceTypes ?? [], [deviceTypeTreeData?.deviceTypes]);
  const deviceTypeOptions = useMemo(() => formatDeviceTypeToDataNode(deviceTypeTree), [deviceTypeTree]);

  const { data: ruleInfo, loading: ruleInfoLoading } = useRequest(
    () => apiV2DataMonitorAlarmRuleInfoPost({ id: Number(id) }),
    {
      ready: isEdit && id !== undefined,
      refreshDeps: [isEdit, id],
    }
  );
  const deviceTypeId = selectedDeviceTypeId ?? (isEdit ? ruleInfo?.deviceTypeId : undefined);
  const boundDeviceIds = isEdit ? ruleInfo?.deviceIds ?? [] : [];
  const dataPropertyDeviceIds = [...new Set([...boundDeviceIds, ...selectedDeviceIds])];
  const dataPropertyDeviceIdsKey = dataPropertyDeviceIds.join(',');

  useEffect(() => {
    if (!ruleInfo) return;

    form.setFieldsValue({
      ruleName: ruleInfo.ruleName,
      alarmLevel: ruleInfo.alarmLevel as AlarmLevel | undefined,
      ruleType: ruleInfo.ruleType as DataMonitoringRuleType | undefined,
      deviceTypePath: findDeviceTypePath(deviceTypeTree, ruleInfo.deviceTypeId),
      physicalModelId: ruleInfo.physicalModelId,
      monitorDataPropertyIds: (ruleInfo.monitorDataProperties ?? []).flatMap(property =>
        property.id === undefined ? [] : [property.id]
      ),
      monitorTimeRange: ruleInfo.monitorTimeRange,
      uploadDataCount: ruleInfo.uploadDataCount,
      alarmFormula: parseOnEcho(ruleInfo.alarmFormula),
      alarmDuration: ruleInfo.alarmDuration,
    });
  }, [deviceTypeTree, form, ruleInfo]);

  const { data: physicalModels = [], loading: physicalModelLoading } = useRequest(
    () =>
      getPhysicalModelList({ deviceTypeIds: [deviceTypeId!] }).then(response =>
        response.list.map(item => ({
          label: item.manufacturerName ? `${item.modelNo}-${item.manufacturerName}` : item.modelNo,
          value: Number(item.id),
        }))
      ),
    {
      ready: deviceTypeId !== undefined,
      refreshDeps: [deviceTypeId],
    }
  );

  const { data: dataProperties = [], loading: dataPropertyLoading } = useRequest(
    () =>
      apiV2DataPropertyListPost({
        deviceTypeId,
        physicalModelId,
        deviceIds: dataPropertyDeviceIds.length ? dataPropertyDeviceIds : undefined,
      }).then(response => {
        const propertyMap = new Map<number, DataPropertyOption>();

        (response.list ?? []).forEach(item => {
          if (item.id !== undefined && item.name) {
            propertyMap.set(item.id, { id: item.id, name: item.name });
          }
        });

        return Array.from(propertyMap.values());
      }),
    {
      ready: deviceTypeId !== undefined,
      refreshDeps: [deviceTypeId, physicalModelId, dataPropertyDeviceIdsKey],
    }
  );

  const { runAsync: saveRule } = useRequest(
    async (values: FormValues) => {
      const deviceIds = [...new Set([...boundDeviceIds, ...(values.deviceIds ?? [])])];
      const commonParams = {
        ruleName: values.ruleName.trim(),
        alarmLevel: values.alarmLevel,
        ruleType: values.ruleType,
      };
      const monitoringParams =
        values.ruleType === DataMonitoringRuleType.COMMUNICATION_STATUS
          ? {
              monitorTimeRange: values.monitorTimeRange,
              uploadDataCount: values.uploadDataCount,
            }
          : (() => {
              const alarmFormula = formatOnSave(values.alarmFormula);
              return {
                alarmFormula,
                alarmDuration: values.alarmDuration,
              };
            })();

      if (isEdit) {
        await apiV2DataMonitorAlarmRuleUpdatePost({
          id: Number(id),
          ...commonParams,
          ...monitoringParams,
          deviceIds,
        }).onError(error => {
          defaultErrorHandling(error);
          throw error;
        });
      } else {
        const alarmFormula =
          values.ruleType === DataMonitoringRuleType.DATA_EXCEPTION ? formatOnSave(values.alarmFormula) : '';
        await apiV2DataMonitorAlarmRuleAddPost({
          ...commonParams,
          ...monitoringParams,
          deviceTypeId,
          physicalModelId: values.physicalModelId,
          monitorDataPropertyIds: values.monitorDataPropertyIds,
          relatedPropertyIds:
            values.ruleType === DataMonitoringRuleType.DATA_EXCEPTION ? getFormulaPropertyIds(alarmFormula) : undefined,
          deviceIds,
        }).onError(error => {
          defaultErrorHandling(error);
          throw error;
        });
      }

      navigate('/device/rule/data-monitoring');
    },
    { manual: true }
  );

  const handleSave = (values: FormValues) => {
    setSaving(true);
    void saveRule(values)
      .finally(() => setSaving(false))
      .catch(() => undefined);
  };

  const cancelEdit = () => {
    Modal.confirm({
      title: '确认取消？',
      content: '取消后未保存的内容将丢失。',
      okText: '确定',
      cancelText: '取消',
      onOk: () => navigate('/device/rule/data-monitoring'),
    });
  };

  const handleSelectedDevicesChange = (deviceIds: number[]) => {
    form.setFieldsValue({
      deviceIds: [...new Set(deviceIds)],
      ...(!isEdit ? { monitorDataPropertyIds: undefined } : {}),
    });
  };

  const propertyOptions = [
    { label: '全选', value: SELECT_ALL_PROPERTIES },
    ...dataProperties.map(item => ({ label: item.name, value: item.id })),
  ];
  const selectableDeviceDataPropertyIds = isEdit
    ? ruleType === DataMonitoringRuleType.COMMUNICATION_STATUS
      ? monitorDataPropertyIds
      : getFormulaPropertyIds(alarmFormula ?? '')
    : undefined;
  const selectedDeviceCount = new Set([...boundDeviceIds, ...selectedDeviceIds]).size;
  const monitorPropertyDisabled = isEdit;
  const pageTitle = isEdit ? '编辑报警规则' : '新建报警规则';

  return (
    <Wrapper
      routes={[...(breadcrumbRoutes?.routes ?? []), { name: pageTitle }]}
      className="page_wrapper"
      isLoading={isEdit && ruleInfoLoading}
    >
      <FormTitle title={pageTitle} />
      <SubContent>
        <Form
          form={form}
          layout="vertical"
          initialValues={{ deviceIds: [] }}
          onFinish={values => handleSave(values as FormValues)}
        >
          <Form.Item name="deviceIds" hidden>
            <Select mode="multiple" />
          </Form.Item>
          <Row gutter={24}>
            <Col span={8}>
              <Form.Item
                name="ruleName"
                label="规则名称"
                rules={[
                  { required: true, whitespace: true, message: '请输入规则名称' },
                  { min: 1, max: 50, message: '规则名称长度为1~50个字符' },
                ]}
              >
                <Input maxLength={50} placeholder="请输入规则名称" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="alarmLevel" label="报警等级" rules={[{ required: true, message: '请选择报警等级' }]}>
                <Select placeholder="请选择报警等级" options={alarmLevelOptions} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="ruleType" label="报警类型" rules={[{ required: true, message: '请选择报警类型' }]}>
                <Select placeholder="请选择报警类型" options={dataMonitoringRuleTypeOptions} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="deviceTypePath"
                label="适用设备类目"
                rules={[{ required: true, message: '请选择适用设备类目' }]}
              >
                <Cascader
                  disabled={isEdit}
                  allowClear={false}
                  showSearch
                  options={deviceTypeOptions}
                  fieldNames={{ label: 'name', value: 'id' }}
                  placeholder="请选择二级设备类目"
                  onChange={() => {
                    form.setFieldsValue({
                      physicalModelId: undefined,
                      monitorDataPropertyIds: undefined,
                      alarmFormula: undefined,
                      deviceIds: [],
                    });
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="physicalModelId" label="物模型型号">
                <Select
                  disabled={isEdit || deviceTypeId === undefined}
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  loading={physicalModelLoading}
                  placeholder={deviceTypeId === undefined ? '请先选择适用设备类目' : '请选择物模型型号'}
                  options={physicalModels}
                  onChange={() => {
                    form.setFieldsValue({
                      monitorDataPropertyIds: undefined,
                      alarmFormula: undefined,
                      deviceIds: [],
                    });
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="选择设备">
                <Space size={8}>
                  <Button type="link" disabled={deviceTypeId === undefined} onClick={() => setDeviceModalOpen(true)}>
                    配置
                  </Button>
                  <span>
                    已选择 <span style={{ color: 'var(--mx-error-color)' }}>{selectedDeviceCount}</span> 个
                  </span>
                </Space>
              </Form.Item>
            </Col>

            {ruleType === DataMonitoringRuleType.COMMUNICATION_STATUS && (
              <>
                <Col span={8}>
                  <Form.Item
                    name="monitorDataPropertyIds"
                    label="监测数据属性"
                    rules={[{ required: true, message: '请选择监测数据属性' }]}
                  >
                    <Select
                      disabled={monitorPropertyDisabled || deviceTypeId === undefined}
                      allowClear
                      mode="multiple"
                      showSearch
                      optionFilterProp="label"
                      maxTagCount="responsive"
                      loading={dataPropertyLoading}
                      placeholder="请选择监测数据属性"
                      options={propertyOptions}
                      onChange={(values: number[]) => {
                        if (values.includes(SELECT_ALL_PROPERTIES)) {
                          form.setFieldValue(
                            'monitorDataPropertyIds',
                            dataProperties.map(item => item.id)
                          );
                        }
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="monitorTimeRange"
                    label="监测时间范围"
                    rules={[{ required: true, message: '请输入监测时间范围' }]}
                  >
                    <InputNumber
                      min={1}
                      max={99999999}
                      precision={0}
                      addonBefore="每"
                      addonAfter="min"
                      placeholder="请输入1~99999999"
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="uploadDataCount"
                    label={
                      <span>
                        上传数据个数
                        <Tooltip title="上传数据个数为在设置的监听时间范围中设备对应监测数据属性至少应该上传的个数；实际上传个数少于该设置个数即触发报警">
                          <InfoCircleOutlined
                            style={{ marginLeft: 5, color: 'var(--mx-warning-color)', cursor: 'pointer' }}
                          />
                        </Tooltip>
                      </span>
                    }
                    rules={[{ required: true, message: '请输入上传数据个数' }]}
                  >
                    <InputNumber
                      min={1}
                      max={99999999}
                      precision={0}
                      addonBefore=">="
                      placeholder="请输入1~99999999"
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>
              </>
            )}

            {ruleType === DataMonitoringRuleType.DATA_EXCEPTION && (
              <>
                <Col span={8}>
                  <Form.Item
                    name="alarmFormula"
                    label={
                      <span>
                        报警公式
                        <Tooltip
                          title={
                            <div>
                              <h3 style={{ color: '#FFFFFF' }}>公式说明</h3>
                              <h4 style={{ color: '#FFFFFF' }}>示例</h4>
                              <div>
                                <div>{`_A相电压 > 10`}</div>
                                <div>{`代表<A相电压>大于10`}</div>
                                <div>{`_A相电压 > 200 && _A相电压 < 240`}</div>
                                <div>{`代表<A相电压>大于200且小于240`}</div>
                                <div>{`avg2(_A相电压, _B相电压) < 200`}</div>
                                <div>{`代表<A相电压>和<B相电压>的平均值小于200`}</div>
                                <div>{`_总有功功率 > $额定功率`}</div>
                                <div>{`代表<总有功功率>大于固定属性-额定功率`}</div>
                                <div>{`_设备状态 == 0`}</div>
                                <div>{`代表<设备状态>离线`}</div>
                                <div>{`dailyTimeInterval(08:00,11:00) && A相电压 > 100`}</div>
                                <div>{`每天8点-10点 满足A相电压>100触发报警`}</div>
                                <div>{`(dailyTimeInterval(08:00,11:00) || dailyTimeInterval(13:00,17:00) ) && A相电压 > 100`}</div>
                                <div>{`每天8点-10点 或 每天13点-17点 满足A相电压>100触发报警`}</div>
                                <div>{`(dailyTimeInterval(08:00,11:00) && weekTimeInterval(1,4)) && A相电压 > 100`}</div>
                                <div>{`每周一到周四 每天8点-10点 满足A相电压>100触发报警`}</div>
                                <div>{`(dailyTimeInterval(08:00,11:00) && A相电压 > 100) || (dailyTimeInterval(13:00,17:00) && A相电压 < 60) `}</div>
                                <div>{`每天8点-10点 满足A相电压>100 或者 每天13点-17点 满足A相电压<60 触发报警`}</div>
                                <div>当输入的报警公式成立时，则设备触发报警。</div>
                              </div>
                              <br />
                              <h4 style={{ color: '#FFFFFF' }}>详细说明</h4>
                              <Space size={[0, 10]} direction="vertical">
                                <div>
                                  <div>{`1. 输入下划线，可以调出数据属性列表，点击选择。`}</div>
                                  <div>{`_A相电压 代表数据属性为A相电压的数据属性，在下文中，统一描述为<数据属性1>`}</div>
                                </div>

                                <div>
                                  <div>{`2. 运算符（按优先级）：`}</div>
                                  <ul>
                                    <li>{`!(非), ~(位取反)`}</li>
                                    <li>{`*(乘), /(除), %(模)`}</li>
                                    <li>{`+(加), -(减)`}</li>
                                    <li>{`<< >> 左移右移`}</li>
                                    <li>{`<(小于), <=(小于等于), >(大于), >=(大于等于)`}</li>
                                    <li>{`==(等于), !=(不等于)`}</li>
                                    <li>{`& 按位与`}</li>
                                    <li>{`^ 按位异或`}</li>
                                    <li>{`| 按位或`}</li>
                                    <li>{`&&(与), ||(或)`}</li>
                                  </ul>
                                </div>

                                <div>
                                  <div>3. 函数表</div>
                                  <ul>
                                    <li>{`avg2(_A相电压, _B相电压)  <数据属性1>和<数据属性2>的平均值`}</li>
                                    <li>{`avg3(_A相电压, _B相电压, 400)  <数据属性1>、<数据属性2>和400的平均值`}</li>
                                    <li>{`avg(_A相电压, _B相电压, 400)  <数据属性1>、<数据属性2>和400的平均值(不限个数)。avg系列函数参数顺序不限。`}</li>
                                    <li>{`max2(_A相电压, 25)  <数据属性1>和25的较大值。`}</li>
                                    <li>{`max3(_A相电压, _B相电压, 25)  <数据属性1>、<数据属性2>和25的较大值。`}</li>
                                    <li>{`max(_A相电压, 25)  <数据属性1>和25的较大值(不限个数).max系列函数参数顺序不限。`}</li>
                                    <li>{`min(_A相电压, 0.0)  <数据属性1>和0.0的较小值。min系列函数和max类似，有min2,min3`}</li>
                                    <li>{`pow(10,_A相电压)  10的<数据属性1>次幂`}</li>
                                    <li>{`abs(_A相电压)  <数据属性1>的绝对值`}</li>
                                    <li>{`sqrt(_A相电压)  <数据属性1>的0.5次幂`}</li>
                                    <li>{`sin(_A相电压)  <数据属性1>的正弦(弧度)`}</li>
                                    <li>{`cos(_A相电压)  <数据属性1>的余弦(弧度)`}</li>
                                    <li>{`tan(_A相电压)  <数据属性1>的正切(弧度)`}</li>
                                    <li>{`asin(_A相电压)  <数据属性1>的反正弦(弧度)`}</li>
                                    <li>{`acos(_A相电压)  <数据属性1>的反余弦(弧度)`}</li>
                                    <li>{`atan(_A相电压)  <数据属性1>的反正切(弧度)`}</li>
                                    <li>{`if(_A相电压, 1, -1) <数据属性1>不为0.0(即_1 != false)时返回1，否则返回-1。`}</li>
                                    <li>{`up(_A相电压) 开关量0->1`}</li>
                                    <li>{`down(_A相电压) 开关量1->0`}</li>
                                    <li>{`dailyTimeInterval(开始时段, 结束时段) 分时段报警(按日) 时间格式为HH:mm, 比如dailyTimeInterval(08:00,10:00)`}</li>
                                    <li>{`weekTimeInterval(开始时段, 结束时段) 分时段报警(按周) 周时间格式为(1-7), 比如weekTimeInterval(1,4)即周一到周四`}</li>
                                  </ul>

                                  <div style={{ margin: '0 0 10px 20px' }}>采集周期：网关配置中的采集间隔</div>
                                  <ul>
                                    <li>{`pAvg(_A相电压) 采集周期内的平均值`}</li>
                                    <li>{`pSum(_A相电压) 采集周期内的总值`}</li>
                                    <li>{`pMax(_A相电压) 采集周期内的最大值`}</li>
                                    <li>{`pMin(_A相电压) 采集周期内的最小值`}</li>
                                    <li>{`pAscend(_A相电压) 采集周期内的上升值`}</li>
                                    <li>{`pDescend(_A相电压) 采集周期内的下降值`}</li>
                                    <li>{`pAbsDif(_A相电压) 采集周期内的绝对值`}</li>
                                    <li>{`smavg(_A相电压,15) 时移平均：第一个参数时点id，第二个参数是时间窗口，单位为秒，具体范围是[now-windows,now]。**参数顺序级类型不能乱**。该函数求数据属性过去15秒内数据的算术平均数。`}</li>
                                  </ul>
                                </div>
                              </Space>
                            </div>
                          }
                        >
                          <InfoCircleOutlined
                            style={{ marginLeft: 5, color: 'var(--mx-warning-color)', cursor: 'pointer' }}
                          />
                        </Tooltip>
                      </span>
                    }
                    validateFirst
                    rules={[{ required: true, message: '请填写报警公式' }, { validator: formulaValidator }]}
                  >
                    <FormulaInput
                      showHelpIcon={false}
                      disabled={deviceTypeId === undefined}
                      dataProperties={dataProperties.map(item => ({
                        id: `x_${item.id}`,
                        display: item.name,
                        description: '建模',
                      }))}
                      fixedProperties={[]}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="alarmDuration"
                    label="报警持续时长"
                    rules={[{ required: true, message: '请输入报警持续时长' }]}
                  >
                    <InputNumber
                      min={0}
                      max={99999999.99}
                      precision={2}
                      addonAfter="s"
                      placeholder="请输入0~99999999.99"
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>
              </>
            )}
          </Row>

          <Space size={8}>
            <Button type="primary" htmlType="submit" loading={saving}>
              保存
            </Button>
            <Button onClick={cancelEdit}>取消</Button>
          </Space>
        </Form>
      </SubContent>

      <SelectDeviceModal
        open={deviceModalOpen}
        isEdit={isEdit}
        ruleId={isEdit ? Number(id) : undefined}
        deviceTypeId={deviceTypeId}
        physicalModelId={physicalModelId}
        dataPropertyIds={selectableDeviceDataPropertyIds}
        boundDeviceIds={boundDeviceIds}
        value={selectedDeviceIds}
        onChange={handleSelectedDevicesChange}
        onClose={() => setDeviceModalOpen(false)}
      />
    </Wrapper>
  );
};

export default DataMonitoringAlarmRuleCreateOrEdit;
